import axiosInstance from '../auth/axiosInstance';
import { getApiErrorMessage } from '../types/api';
import {
  normalizeCapabilitiesList,
  normalizeCapabilityFromApi,
} from '../utils/capabilityApiNormalize';
import { mapQuestionsForSave } from '../utils/subCapabilityQuestionUtils';
import type { CapabilityCatalogQuery } from '../utils/capabilityCatalogUtils';
import {
  normalizeCapabilityCatalogFilterOptions,
  normalizeCapabilityCatalogPage,
} from '../utils/capabilityCatalogUtils';
import { invalidateAfterCapabilitySelectionChange } from '../utils/clientProjectDataCache';

export type CapabilityApprovalRequest = {
  userName: string;
  projectName: string;
  status: string;
  comment: string;
  requestDate?: string;
  approvedDate?: string;
};

export type CapabilityApprovalUpdatePayload = {
  userName: string;
  projectName: string;
  status: 'Approved' | 'Rejected' | 'Pending';
  comment: string;
};

const normalizeCapabilityApprovalRequest = (item: Record<string, unknown>): CapabilityApprovalRequest => ({
  userName: String(item.userName ?? item.UserName ?? ''),
  projectName: String(item.projectName ?? item.ProjectName ?? ''),
  status: String(item.status ?? item.Status ?? ''),
  comment: String(item.comment ?? item.Comment ?? item.comments ?? item.Comments ?? ''),
  requestDate: (item.requestDate ?? item.RequestDate) as string | undefined,
  approvedDate: (item.approvedDate ?? item.ApprovedDate) as string | undefined,
});

export const fetchCapabilities = async (projectId) => {
  try {
    const { data } = await axiosInstance.get(
      `/capability/get-all-capabilities/${projectId}`
    );

    if (data?.succeeded === false) {
      throw new Error(data.message || 'Failed to fetch capabilities');
    }

    return {
      ...data,
      data: normalizeCapabilitiesList(data?.data),
    };
  } catch (error) {
    console.error('Error fetching capabilities:', error);
    throw new Error(getApiErrorMessage(error));
  }
};

const catalogQueryParams = (query: CapabilityCatalogQuery) => {
  const params: Record<string, string | number> = {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
    sort: query.sort || 'framework-asc',
  };

  if (query.search) params.search = query.search;
  if (query.frameworkId) params.frameworkId = query.frameworkId;
  if (query.sourceTypeId) params.sourceTypeId = query.sourceTypeId;
  if (query.capabilityName) params.capabilityName = query.capabilityName;
  if (query.capabilityIsActive) params.capabilityIsActive = query.capabilityIsActive;
  if (query.frameworkType) params.frameworkType = query.frameworkType;
  if (query.sourceFrameworkName) params.sourceFrameworkName = query.sourceFrameworkName;
  if (query.frameworkReferenceCompany) params.frameworkReferenceCompany = query.frameworkReferenceCompany;
  if (query.businessTypeName) params.businessTypeName = query.businessTypeName;
  if (query.subCapabilityCount) params.subCapabilityCount = query.subCapabilityCount;
  if (query.questionCount) params.questionCount = query.questionCount;

  return params;
};

export const getCapabilityCatalog = async (query: CapabilityCatalogQuery) => {
  try {
    const { data: result } = await axiosInstance.get('/Capability/GetCapabilityCatalog', {
      params: catalogQueryParams(query),
    });

    if (result?.succeeded === false) {
      throw new Error(result.message || 'Failed to fetch capability catalog');
    }

    return normalizeCapabilityCatalogPage(result.data as Record<string, unknown>);
  } catch (error) {
    console.error('Error fetching capability catalog:', error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const getCapabilityCatalogFilterOptions = async (query: CapabilityCatalogQuery) => {
  try {
    const { data: result } = await axiosInstance.get('/Capability/GetCapabilityCatalogFilterOptions', {
      params: catalogQueryParams({ ...query, pageNumber: 1, pageSize: 1 }),
    });

    if (result?.succeeded === false) {
      throw new Error(result.message || 'Failed to fetch catalog filters');
    }

    return normalizeCapabilityCatalogFilterOptions(result.data as Record<string, unknown>);
  } catch (error) {
    console.error('Error fetching capability catalog filter options:', error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const getCapabilityDetail = async (capabilityId: number) => {
  try {
    const { data: result } = await axiosInstance.get(`/Capability/GetCapabilityDetail/${capabilityId}`);

    if (result?.succeeded === false) {
      throw new Error(result.message || 'Failed to fetch capability details');
    }

    if (!result.data) {
      throw new Error('Capability not found');
    }

    return normalizeCapabilityFromApi(result.data);
  } catch (error) {
    console.error('Error fetching capability detail:', error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const GetAllCapabilities = async (page, pageSize, sort = 'name-asc', search = '', frameworkId = '') => {
  try {
    const { data: result } = await axiosInstance.get('/Capability/GetAllCapabilities', {
      params: {
        pageNumber: page,
        pageSize,
        sort,
        search,
        ...(Number(frameworkId) > 0 ? { frameworkId: Number(frameworkId) } : {}),
      },
    });

    if (result?.succeeded === false) {
      throw new Error(result.message || 'Failed to fetch capabilities');
    }

    if (
      result.data &&
      Array.isArray(result.data.items) &&
      typeof result.data.totalCount === 'number'
    ) {
      const {
        items,
        totalCount,
        pageNumber,
        pageSize: returnedSize,
      } = result.data;
      return {
        items: normalizeCapabilitiesList(items),
        totalCount,
        pageNumber,
        pageSize: returnedSize,
      };
    }

    console.error('Unexpected response structure:', result);
    return { items: [], totalCount: 0, pageNumber: page, pageSize };
  } catch (error) {
    console.error('Error fetching capabilities:', error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const deleteCapability = async (capabilityId: number) => {
  try {
    const { data: responseData } = await axiosInstance.post(
      `/Capability/DeleteCapability/${capabilityId}`
    );

    if (responseData?.succeeded === false) {
      throw new Error(responseData.message || 'Failed to delete capability');
    }

    return responseData;
  } catch (error) {
    console.error('Error deleting capability:', error);

    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      throw new Error(
        'Delete endpoint not found. Stop the API in Visual Studio, rebuild the ReimagineAPI solution, start it again, then retry.'
      );
    }

    throw new Error(getApiErrorMessage(error));
  }
};

export const exportCapability = async (capabilityId: number) => {
  try {
    const response = await axiosInstance.get(`/Capability/export/${capabilityId}`, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json') || contentType.includes('text/plain')) {
      const text = await response.data.text();
      let message = text || 'Failed to export capability.';
      try {
        message = JSON.parse(text)?.message || message;
      } catch {
        // keep text
      }
      throw new Error(message);
    }

    const disposition = response.headers['content-disposition'] || '';
    const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : 'capability.xlsx';

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.remove();
  } catch (error) {
    if (error instanceof Error && error.message && !('response' in error)) {
      throw error;
    }
    throw new Error(getApiErrorMessage(error) || 'Failed to export capability.');
  }
};

export const importCapabilities = async (file) => {
  if (!file) {
    throw new Error('Please choose an Excel file to import.');
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await axiosInstance.post('/Capability/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (data?.succeeded === false) {
      throw new Error(data.message || 'Failed to import capabilities.');
    }

    const payload = data?.data ?? data;
    return {
      createdCount: payload?.createdCount ?? payload?.CreatedCount ?? 0,
      errors: payload?.errors ?? payload?.Errors ?? [],
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to import capabilities.');
  }
};

export const addOrUpdateCapability = async (capabilityData) => {
  try {
    const blankToNull = (value) => {
      if (value == null) return null;
      const trimmed = String(value).trim();
      return trimmed.length ? trimmed : null;
    };

    const serializeMaturityForSave = (item) => {
      const useCustomMaturity = Boolean(item?.useCustomMaturity);
      const maturityOptionsJson =
        item?.maturityOptionsJson ??
        (useCustomMaturity && Array.isArray(item?.maturityOptions)
          ? JSON.stringify(
              item.maturityOptions.map((option, index) => ({
                optionValue: option.optionValue || index + 1,
                label: (option.label || '').trim(),
                definition: (option.definition || '').trim(),
              }))
            )
          : null);
      return blankToNull(maturityOptionsJson);
    };

    const mapSubCapability = (subCap) => {
      const parentId = capabilityData.id > 0 ? capabilityData.id : null;

      return {
        id: subCap.subId ?? subCap.id ?? 0,
        capabilityId: parentId,
        name: subCap.subCapabilityName ?? subCap.name ?? '',
        description: subCap.subCapabilityDescription ?? subCap.description ?? '',
        iconURL: subCap.iconURL ?? subCap.capabilityIconUrl ?? '',
        isActive: Boolean(subCap.isActive ?? true),
        currentStateCommentPlaceholder: blankToNull(subCap.currentStateCommentPlaceholder),
        futureStateCommentPlaceholder: blankToNull(subCap.futureStateCommentPlaceholder),
        currentStateQuestions: mapQuestionsForSave(subCap.currentStateQuestions),
        futureStateQuestions: mapQuestionsForSave(subCap.futureStateQuestions),
        maturityOptionsJson: serializeMaturityForSave(subCap),
        subCapabilities: [],
      };
    };

    const namedSubs = (capabilityData.subCapabilities ?? []).filter((subCap) =>
      String(subCap.subCapabilityName ?? subCap.name ?? '').trim()
    );
    const hasNamedSubs = namedSubs.length > 0;

    const formattedData = {
      id: capabilityData.id ?? 0,
      businessTypeId: capabilityData.businessTypeId,
      frameworkId: capabilityData.frameworkId ? Number(capabilityData.frameworkId) : null,
      capabilityId: null,
      name: capabilityData.capabilityName ?? capabilityData.name ?? '',
      iconURL:
        capabilityData.capabilityIconUrl ??
        capabilityData.iconURL ??
        'images/future_icon.png',
      description:
        capabilityData.capabilityDescription ?? capabilityData.description ?? '',
      isActive: Boolean(capabilityData.isActive),
      currentStateQuestions: hasNamedSubs
        ? []
        : mapQuestionsForSave(capabilityData.currentStateQuestions),
      futureStateQuestions: hasNamedSubs
        ? []
        : mapQuestionsForSave(capabilityData.futureStateQuestions),
      currentStateCommentPlaceholder: hasNamedSubs
        ? null
        : blankToNull(capabilityData.currentStateCommentPlaceholder),
      futureStateCommentPlaceholder: hasNamedSubs
        ? null
        : blankToNull(capabilityData.futureStateCommentPlaceholder),
      maturityOptionsJson: hasNamedSubs ? null : serializeMaturityForSave(capabilityData),
      subCapabilities: namedSubs.map(mapSubCapability),
    };

    const { data: responseData } = await axiosInstance.post(
      '/Capability/AddOrUpdateCapabilities',
      formattedData
    );

    if (responseData?.succeeded === false) {
      throw new Error(responseData.message || 'Failed to save capability');
    }

    if (responseData.data) {
      return normalizeCapabilityFromApi(responseData.data);
    }

    console.error('Invalid response structure:', responseData);
    return {
      ...formattedData,
      id: formattedData.id || `temp-${Date.now()}`,
    };
  } catch (error) {
    console.error('Error during saving capability:', error.message || error);
    throw new Error(getApiErrorMessage(error));
  }
};

export const submitCapabilities = async (payload) => {
  try {
    const { data } = await axiosInstance.post(
      '/Capability/add-or-update-selected-capabilities',
      payload
    );
    if (payload?.userId != null && payload?.projectId != null) {
      invalidateAfterCapabilitySelectionChange(payload.userId, payload.projectId);
    }
    return data;
  } catch (error) {
    console.error('Error submitting capabilities:', error);
    throw error;
  }
};

export const fetchSelectedCapabilities = async (payload) => {
  try {
    const { data } = await axiosInstance.post(
      '/Capability/get-selected-capabilities',
      payload
    );
    return data;
  } catch (error) {
    console.error('Error fetching selected capabilities:', error);
    throw error;
  }
};

export const getCapabilityRequestData = async (
  searchTerm = '',
  status = '',
  page = 1,
  pageSize = 10,
  sort = ''
) => {
  try {
    const { data: result } = await axiosInstance.post(
      '/Capability/GetCapabilityRequestData',
      null,
      {
        params: {
          ...(searchTerm && { searchTerm }),
          ...(status && { status }),
          ...(sort && { sort }),
          page,
          pageSize,
        },
      }
    );

    console.log('Capability request data response:', result);

    if (result.succeeded && result.data) {
      const rawItems = Array.isArray(result.data.data) ? result.data.data : [];
      return {
        data: rawItems.map((item) =>
          normalizeCapabilityApprovalRequest(item as Record<string, unknown>)
        ),
        totalRecords: result.data.totalRecords || 0,
        pageNumber: result.data.pageNumber || page,
        pageSize: result.data.pageSize || pageSize,
        totalPages: result.data.totalPages || 1,
        hasNextPage: result.data.hasNextPage || false,
        hasPreviousPage: result.data.hasPreviousPage || false,
      };
    }

    console.error('Unexpected response structure:', result);
    return {
      data: [],
      totalRecords: 0,
      pageNumber: page,
      pageSize: pageSize,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  } catch (error) {
    console.error('Error fetching capability request data:', error);
    throw error;
  }
};

export const updateCapabilityRequest = async (
  requestData: CapabilityApprovalUpdatePayload
) => {
  try {
    const { data: result } = await axiosInstance.post(
      '/Capability/add-capability-request',
      {
        userId: 0,
        projectId: 0,
        userName: requestData.userName,
        projectName: requestData.projectName,
        status: requestData.status,
        comment: requestData.comment,
      }
    );

    if (result?.succeeded === false) {
      throw new Error(result.message || 'Failed to update capability request');
    }

    return result;
  } catch (error) {
    console.error('Error updating capability request:', error);
    throw error;
  }
};
